// The front page's visual (02-DESIGN/03-console/03-the-signed-out-site.md
// § the visual; decision 0071 § 3): a cloud drawn as a wireframe — points on
// the surface of a few overlapping spheres with a flat base, each joined to
// its nearest neighbours — in the brand's grey, turning about its vertical
// axis once in about a minute, seen from slightly above. On arrival the
// points assemble from scatter, staggered, over about two seconds; after it,
// a few teal signals travel its edges with short fading trails, and a dotted
// stream flows into its base from the bottom left. Seeded, so it is the same
// cloud on every visit; paused while the page is hidden or the figure is
// scrolled out of view; one composed frame, with nothing turning or drifting,
// for a person who prefers reduced motion. Ported from the prototype the
// owner approved (specs/060-the-front-page/prototype.html).

import {
  at,
  dist3,
  easeOut,
  fit,
  inkOf,
  mulberry32,
  REDUCED_MOTION,
  reducedMotion,
  type Vec3,
} from "./draw.ts";

/** The figure, built once from a seed. */
export interface Cloud {
  readonly pts: readonly Vec3[];
  readonly edges: readonly (readonly [number, number])[];
  /** The edges at each point, for a signal choosing where to go next. */
  readonly adj: readonly (readonly number[])[];
  /** Where each point starts before the figure assembles. */
  readonly scatter: readonly Vec3[];
  readonly size: readonly number[];
  /** Each point's stagger, in seconds. */
  readonly delay: readonly number[];
}

/** The spheres the cloud is the surface of, and the flat base under them. */
const SPHERES = [
  { x: 0.0, y: 0.12, z: 0.0, r: 1.0 },
  { x: -1.0, y: -0.1, z: 0.15, r: 0.8 },
  { x: 1.05, y: -0.05, z: -0.1, r: 0.86 },
  { x: 0.55, y: 0.55, z: 0.25, r: 0.62 },
  { x: -0.45, y: 0.5, z: -0.25, r: 0.56 },
  { x: 1.9, y: -0.3, z: 0.05, r: 0.55 },
  { x: -1.9, y: -0.32, z: -0.05, r: 0.5 },
  { x: 0.1, y: -0.2, z: 0.62, r: 0.7 },
  { x: -0.3, y: -0.22, z: -0.62, r: 0.66 },
] as const;
const BASE = -0.64;

/** The seed the page's cloud is built from. */
export const CLOUD_SEED = 7;

/** buildCloud places the points and joins each to its nearest neighbours; the same seed, the same cloud. */
export function buildCloud(seed: number = CLOUD_SEED): Cloud {
  const rand = mulberry32(seed);
  const pts: Vec3[] = [];
  const near = (p: Vec3, d: number) => pts.some((q) => dist3(p, q) < d);

  // The surface: a point on one sphere that lies inside no other, above the base, apart from the rest.
  for (let tries = 0; pts.length < 235 && tries < 60000; tries++) {
    const s = at(SPHERES, Math.floor(rand() * SPHERES.length));
    const u = rand() * 2 - 1;
    const phi = rand() * Math.PI * 2;
    const rr = Math.sqrt(1 - u * u);
    const p: Vec3 = [s.x + s.r * rr * Math.cos(phi), s.y + s.r * u, s.z + s.r * rr * Math.sin(phi)];
    if (p[1] < BASE) {
      continue;
    }
    if (SPHERES.some((o) => o !== s && Math.hypot(p[0] - o.x, p[1] - o.y, p[2] - o.z) < o.r * 0.985)) {
      continue;
    }
    if (near(p, 0.17)) {
      continue;
    }
    pts.push(p);
  }
  // The flat base: points on the plane under the union of the spheres.
  for (let nb = 0, tries = 0; nb < 60 && tries < 40000; tries++) {
    const x = rand() * 5.4 - 2.7;
    const z = rand() * 2.8 - 1.4;
    const pb: Vec3 = [x, BASE, z];
    if (!SPHERES.some((o) => Math.hypot(x - o.x, BASE - o.y, z - o.z) < o.r * 0.97)) {
      continue;
    }
    if (near(pb, 0.2)) {
      continue;
    }
    pts.push(pb);
    nb++;
  }

  const edges: [number, number][] = [];
  const seen = new Set<string>();
  const adj: number[][] = pts.map(() => []);
  for (let i = 0; i < pts.length; i++) {
    const pi = at(pts, i);
    const d: [number, number][] = [];
    for (let j = 0; j < pts.length; j++) {
      if (i !== j) {
        const dd = dist3(pi, at(pts, j));
        if (dd < 0.66) {
          d.push([dd, j]);
        }
      }
    }
    d.sort((a, b) => a[0] - b[0]);
    for (const [, j] of d.slice(0, 4)) {
      const key = i < j ? `${i}-${j}` : `${j}-${i}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      edges.push([i, j]);
      at(adj, i).push(edges.length - 1);
      at(adj, j).push(edges.length - 1);
    }
  }
  const scatter = pts.map((p): Vec3 => [
    p[0] + (rand() - 0.5) * 3.2,
    p[1] + (rand() - 0.5) * 3.2,
    p[2] + (rand() - 0.5) * 3.2,
  ]);
  const size = pts.map(() => (rand() < 0.14 ? 1.7 : 1));
  const delay = pts.map(() => rand() * 0.7);
  return { pts, edges, adj, scatter, size, delay };
}

/** The angle the figure is first seen at. */
const START_TURN = 0.35;
/** One turn in about a minute. */
const TURN_PER_SECOND = (Math.PI * 2) / 64;

/** turnAt is the figure's angle t seconds after arrival; under reduced motion it never turns. */
export function turnAt(t: number, reduce: boolean): number {
  return reduce ? START_TURN : START_TURN + t * TURN_PER_SECOND;
}

/** assemblyAt is how far a point with this delay has come from scatter, 0 to 1; under reduced motion it has arrived. */
export function assemblyAt(t: number, delay: number, reduce: boolean): number {
  return reduce ? 1 : easeOut((t - 0.1 - delay) / 1.5);
}

/** The dots of the stream, and how far along its curve they have drifted t seconds after arrival. */
const STREAM_DOTS = 64;
export function driftAt(t: number, reduce: boolean): number {
  return reduce ? 0 : (t * 0.05) % (1 / STREAM_DOTS);
}

/** The signals a still frame holds in place: an edge and how far along it. */
const STILL: readonly (readonly [number, number])[] = [
  [12, 0.3],
  [80, 0.6],
  [140, 0.5],
  [200, 0.2],
  [260, 0.7],
];

interface Signal {
  edge: number;
  t: number;
  dir: 1 | -1;
  speed: number;
  life: number;
  trail: [number, number][];
}

/**
 * drawCloud draws the page's cloud on a canvas and keeps it turning while it
 * is in view and the page is visible; it returns what stops it.
 */
export function drawCloud(canvas: HTMLCanvasElement): () => void {
  const ctx = canvas.getContext("2d");
  if (ctx === null) {
    return () => undefined;
  }
  const cloud = buildCloud();
  const ink = inkOf(canvas);
  const prand = mulberry32(99);
  const n = cloud.pts.length;
  const proj: [number, number, number][] = cloud.pts.map(() => [0, 0, 0]);
  const shown: number[] = cloud.pts.map(() => 0);
  let signals: Signal[] = [];
  let box = fit(canvas);
  let reduce = reducedMotion();
  let t0 = performance.now();
  let frame = 0;
  let running = false;
  let inView = true;

  const newSignal = (): Signal => ({
    edge: Math.floor(prand() * cloud.edges.length),
    t: 0,
    dir: prand() < 0.5 ? 1 : -1,
    speed: 0.012 + prand() * 0.012,
    life: 0,
    trail: [],
  });
  const step = (s: Signal): Signal => {
    s.t += s.speed;
    s.life += 1;
    if (s.t < 1) {
      return s;
    }
    const e = at(cloud.edges, s.edge);
    const end = s.dir === 1 ? e[1] : e[0];
    const next = at(cloud.adj, end).filter((x) => x !== s.edge);
    if (next.length === 0 || s.life > 420) {
      return newSignal();
    }
    const ne = at(next, Math.floor(prand() * next.length));
    s.edge = ne;
    s.t = 0;
    s.dir = at(cloud.edges, ne)[0] === end ? 1 : -1;
    return s;
  };
  const dot = (x: number, y: number, r: number, rgb: string, a: number) => {
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${rgb},${a.toFixed(3)})`;
    ctx.fill();
  };

  const draw = (now: number) => {
    const t = (now - t0) / 1000;
    const { w: W, h: H, dpr } = box;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    const narrow = W < 640;
    const scale = Math.min(W / (narrow ? 5.9 : 5.4), H / 3.0);
    const cx = W * (narrow ? 0.5 : 0.54);
    const cy = H * 0.44;
    const theta = turnAt(t, reduce);
    const cs = Math.cos(theta);
    const sn = Math.sin(theta);
    const tilt = -0.26;
    const tc = Math.cos(tilt);
    const ts = Math.sin(tilt);
    let assembled = 1;

    for (let i = 0; i < n; i++) {
      const e = assemblyAt(t, at(cloud.delay, i), reduce);
      assembled = Math.min(assembled, e);
      const p = at(cloud.pts, i);
      const s = at(cloud.scatter, i);
      const x = s[0] + (p[0] - s[0]) * e;
      const y = s[1] + (p[1] - s[1]) * e;
      const z = s[2] + (p[2] - s[2]) * e;
      const rx = x * cs + z * sn;
      const rz = -x * sn + z * cs;
      const ry = y * tc - rz * ts;
      const rz2 = y * ts + rz * tc;
      const f = 4.2 / (4.2 + rz2);
      const pr = at(proj, i);
      pr[0] = cx + rx * f * scale;
      pr[1] = cy - ry * f * scale;
      pr[2] = (rz2 + 2.4) / 4.8; // 0 near … 1 far
      shown[i] = e;
    }

    // The stream: a dotted curve from the words into the base of the cloud, the dots growing as they near it.
    const sx = W * 0.02;
    const sy = H * 0.98;
    const ex = cx - 0.2 * scale;
    const ey = cy + 0.64 * scale * 0.9 + 6;
    const c1x = W * 0.16;
    const c1y = H * 0.74;
    const c2x = W * 0.3;
    const c2y = H * 0.9;
    const drift = driftAt(t, reduce);
    for (let k = 0; k < STREAM_DOTS; k++) {
      const u = k / STREAM_DOTS + drift;
      if (u > 1) {
        continue;
      }
      const mu = 1 - u;
      const px = mu * mu * mu * sx + 3 * mu * mu * u * c1x + 3 * mu * u * u * c2x + u * u * u * ex;
      const py = mu * mu * mu * sy + 3 * mu * mu * u * c1y + 3 * mu * u * u * c2y + u * u * u * ey;
      dot(px, py, 0.5 + 1.7 * u * u, ink.mesh, (0.25 + 0.5 * u) * assembled);
    }

    // The edges, the far ones fainter.
    ctx.lineWidth = 0.9;
    for (const [a, b] of cloud.edges) {
      const A = at(proj, a);
      const B = at(proj, b);
      const nearness = 1 - Math.min(1, Math.max(0, (A[2] + B[2]) / 2));
      const al = (0.14 + 0.42 * nearness) * Math.min(at(shown, a), at(shown, b));
      if (al <= 0.01) {
        continue;
      }
      ctx.strokeStyle = `rgba(${ink.mesh},${al.toFixed(3)})`;
      ctx.beginPath();
      ctx.moveTo(A[0], A[1]);
      ctx.lineTo(B[0], B[1]);
      ctx.stroke();
    }
    // The points, the near ones larger and darker.
    for (let i = 0; i < n; i++) {
      const P = at(proj, i);
      const nearness = 1 - Math.min(1, Math.max(0, P[2]));
      const a = (0.35 + 0.55 * nearness) * at(shown, i);
      if (a > 0.01) {
        dot(P[0], P[1], (1.0 + 1.9 * nearness) * at(cloud.size, i), ink.mesh, a);
      }
    }

    // The signals: calls travelling the substrate, once it has assembled.
    if (reduce) {
      for (const [edge, along] of STILL) {
        const E = at(cloud.edges, edge % cloud.edges.length);
        const F = at(proj, E[0]);
        const T = at(proj, E[1]);
        dot(F[0] + (T[0] - F[0]) * along, F[1] + (T[1] - F[1]) * along, 2.6, ink.teal, 0.85);
      }
    } else if (assembled >= 1) {
      while (signals.length < 7) {
        signals.push(newSignal());
      }
      signals = signals.map(step);
      for (const s of signals) {
        const E = at(cloud.edges, s.edge);
        const from = at(proj, s.dir === 1 ? E[0] : E[1]);
        const to = at(proj, s.dir === 1 ? E[1] : E[0]);
        const gx = from[0] + (to[0] - from[0]) * s.t;
        const gy = from[1] + (to[1] - from[1]) * s.t;
        const gz = from[2] + (to[2] - from[2]) * s.t;
        const gn = 1 - Math.min(1, Math.max(0, gz));
        s.trail.unshift([gx, gy]);
        if (s.trail.length > 7) {
          s.trail.pop();
        }
        for (let k = s.trail.length - 1; k >= 1; k--) {
          const [tx, ty] = at(s.trail, k);
          dot(tx, ty, 1.4 + 0.8 * gn, ink.teal, (0.08 + 0.3 * gn) * (1 - k / s.trail.length));
        }
        dot(gx, gy, 1.8 + 1.2 * gn, ink.teal, 0.45 + 0.5 * gn);
      }
    }
  };

  const loop = (now: number) => {
    if (!running) {
      return;
    }
    draw(now);
    frame = window.requestAnimationFrame(loop);
  };
  const start = () => {
    if (running || reduce || !inView || document.hidden) {
      return;
    }
    running = true;
    frame = window.requestAnimationFrame(loop);
  };
  const stop = () => {
    running = false;
    window.cancelAnimationFrame(frame);
  };
  // One composed frame: assembled, a few signals in place, no turning, no drift.
  const still = () => {
    stop();
    draw(t0 + 5000);
  };
  const settle = () => {
    if (reduce) {
      still();
    } else {
      start();
    }
  };

  const onVisibility = () => {
    if (document.hidden) {
      stop();
    } else {
      start();
    }
  };
  document.addEventListener("visibilitychange", onVisibility);

  const io =
    typeof IntersectionObserver === "function"
      ? new IntersectionObserver(
          (entries) => {
            inView = entries.some((en) => en.isIntersecting);
            if (inView) {
              start();
            } else {
              stop();
            }
          },
          { threshold: 0.02 },
        )
      : undefined;
  io?.observe(canvas);

  const ro =
    typeof ResizeObserver === "function"
      ? new ResizeObserver(() => {
          box = fit(canvas);
          if (!running) {
            draw(reduce ? t0 + 5000 : performance.now());
          }
        })
      : undefined;
  ro?.observe(canvas);

  // A change of the preference while the page is open is honoured at once.
  const mq = typeof window.matchMedia === "function" ? window.matchMedia(REDUCED_MOTION) : undefined;
  const onPreference = () => {
    reduce = mq?.matches ?? false;
    t0 = performance.now();
    signals = [];
    settle();
  };
  mq?.addEventListener("change", onPreference);

  settle();

  return () => {
    stop();
    document.removeEventListener("visibilitychange", onVisibility);
    io?.disconnect();
    ro?.disconnect();
    mq?.removeEventListener("change", onPreference);
  };
}
